import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import { Box } from '@chakra-ui/react';
import { ADD_VIOLATION_DRAWER_QUERY_KEY } from 'Deer/components/DisciplinaryAction/BottomDrawers/CreateViolationDrawer/constants';
import { ViewViolationCommentModal } from 'Deer/components/DisciplinaryAction/BottomDrawers/ViewViolationDrawer/components/ViewViolationCommentModal';
import {
  useEvaluateNote,
  useBulkEvaluateNotes,
} from 'Deer/components/DisciplinaryAction/BottomDrawers/ViewViolationDrawer/dataHooks';
import { useSetDisciplinaryActionMetaFiltersInitialData } from 'Deer/intent/DisciplinaryActions/hooks/disciplinaryActionGrid';
import {
  useSelectDisciplinaryActionsSelectedCompanies,
  useSelectDisciplinaryActionsSelectedLocations,
} from 'Deer/intent/DisciplinaryActions/slice/DisciplinaryActionsIntentSlice';
import {
  useGetDisciplinaryActions,
  useInvalidateGetDisciplinaryActions,
} from 'Deer/pages/MyData/DisciplinaryActions/dataHooks';
import {
  DISCIPLINARY_ACTIONS_COLUMN_ORDER,
  DISCIPLINARY_ACTIONS_STATUS,
} from 'Deer/pages/Team/DirectReports/DisciplinaryActions/constants';
import type { GridApi, RowClickedEvent } from 'ag-grid-community';
import { useToast } from 'components/hooks/useToast';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';
import { getColumnDefinitionByType } from 'screens/performanceReview/manageAndInsights/TalentPlanning/columnDefinitions';
import { checkEnableCompanySupport } from 'utils/helpers/featureFlags';

import NoDisciplinaryActions from 'assets/assetsV1/illustrations/noDisciplinaryActions.svg';

import { DISCIPLINARY_ACTIONS_INTENT_ID_MAP } from 'constants/moduleIntentTypes';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { AgGridKebabMenuOptions } from 'uiToolkitV1/AgGridWrapperV2/types';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';

import { ApproveAllConfirmationModal } from './components/ApproveAllConfirmationModal';
import { useBulkActionContext } from './components/BulkActionHeader/BulkActionContext';
import { BulkActionHeader } from './components/BulkActionHeader/DisciplinaryActionsBulkActionHeader';
import { DenyAllConfirmationModal } from './components/DenyAllConfirmationModal';
import { NotifyEmployeeConfirmationDrawer } from './components/NotifyEmployeeConfirmationDrawer';
import { useNotifyPeople } from './dataHooks';

// Proper TypeScript interfaces
interface MyCompanyDisciplinaryActionsGridProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gridRef?: React.RefObject<any>;
  gridStatePersistenceKey: string;
  intentId?: string;
}

interface ColumnProps {
  columnId: string;
  displayName: string;
  description?: string;
  [key: string]: unknown;
}

interface ColumnDef {
  colId: string;
  field: string;
  id: string;
  displayName: string;
  description?: string;
  minWidth: number;
  [key: string]: unknown;
}

export type DisciplinaryNoteEmployee = {
  id: number;
  companyId: number;
  displayName: string;
  profilePhoto: string;
  designation: string;
  ownerType: string | null;
  employeeFunction: string | null;
  email: string;
  preferredName: string;
};

export type DisciplinaryNoteRow = {
  LOCATION: string;
  STATUS: string;
  RAISED_BY: DisciplinaryNoteEmployee;
  RAISED_FOR: DisciplinaryNoteEmployee;
  DESCRIPTION: string;
  REPORTED_TIME: string;
  NOTE_ID: number;
  TYPE: string;
  COMPANY: {
    id: number;
    companyName: string;
  };
};

function MyCompanyDisciplinaryActionsGrid({
  gridRef,
  gridStatePersistenceKey,
  intentId,
}: MyCompanyDisciplinaryActionsGridProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentCompRef = useRef<any>(null);
  const agGridRef = gridRef ?? currentCompRef;
  const cohortDetails = useSelectedView();
  const history = useHistory();
  const toast = useToast();

  const [columnDefs, setColumnDefs] = useState<ColumnDef[]>([]);
  const [denyModalState, setDenyModalState] = useState<{ open: boolean; noteId: number | null; isDenyAll: boolean }>({
    open: false,
    noteId: null,
    isDenyAll: false,
  });
  const [notifyEmployeeConfirmationDrawerState, setNotifyEmployeeConfirmationDrawerState] = useState<{
    open: boolean;
  }>({
    open: false,
  });
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);

  const {
    isFetching,
    data: disciplinaryActionsData,
    error,
  } = useGetDisciplinaryActions({
    cohortId: cohortDetails?.id,
    status: intentId === DISCIPLINARY_ACTIONS_INTENT_ID_MAP.ALL ? undefined : 'pending',
  });
  const { mutate: evaluateNote, isLoading: isEvaluateNoteLoading } = useEvaluateNote();
  const { mutate: bulkEvaluateNotes, isLoading: isBulkEvaluateNotesLoading } = useBulkEvaluateNotes();
  const { mutate: notifyPeople } = useNotifyPeople();

  const {
    setTotalDisciplinaryActions,
    setSelectedDisciplinaryActionIds,
    resetSelection,
    selectedDisciplinaryActionIds,
    totalDisciplinaryActions,
  } = useBulkActionContext();

  const { search } = useLocation();

  useSetDisciplinaryActionMetaFiltersInitialData(disciplinaryActionsData);
  const selectedCompanies = useSelectDisciplinaryActionsSelectedCompanies();
  const selectedLocations = useSelectDisciplinaryActionsSelectedLocations();

  const { columns, rows = [] } = disciplinaryActionsData ?? {};
  const filteredRows = useMemo(() => {
    if (checkEnableCompanySupport()) {
      const selectedCompaniesMap = {};
      const selectedLocationsMap = {};
      selectedCompanies?.forEach((company) => {
        selectedCompaniesMap[company.id] = company;
      });
      selectedLocations?.forEach((location) => {
        selectedLocationsMap[location?.toLowerCase()] = location;
      });

      return rows?.filter(
        (row) => selectedCompaniesMap[row?.COMPANY?.id] && selectedLocationsMap[row?.LOCATION?.toLowerCase()]
      );
    }
    return rows;
  }, [rows, selectedCompanies, selectedLocations]);

  // Handle filter and data changes for total count
  const handleFilterAndDataChange = useCallback(() => {
    let total = 0;
    const api = agGridRef.current?.api as GridApi | undefined;

    if (api?.isAnyFilterPresent()) {
      api.forEachNodeAfterFilter(() => {
        total += 1;
      });
    } else {
      total = filteredRows?.length || 0;
    }
    setTotalDisciplinaryActions(total);
  }, [agGridRef, filteredRows?.length, setTotalDisciplinaryActions]);

  // Handle clearing selection
  const handleClearSelection = useCallback(() => {
    const api = agGridRef.current?.api as GridApi | undefined;
    api?.deselectAll();
  }, [agGridRef]);

  // Handle row data updates
  const onRowDataUpdated = useCallback(() => {
    resetSelection();
    handleClearSelection();
    handleFilterAndDataChange();
  }, [handleFilterAndDataChange, resetSelection, handleClearSelection]);

  // when the filtered rows are 0, aggrid does not trigger the onRowDataUpdated event
  // so we need to manually trigger it
  useEffect(() => {
    if (filteredRows?.length === 0 && totalDisciplinaryActions > 0) {
      onRowDataUpdated();
    }
  }, [filteredRows, onRowDataUpdated, setTotalDisciplinaryActions, totalDisciplinaryActions]);

  // Get the invalidate function
  const invalidateDisciplinaryActions = useInvalidateGetDisciplinaryActions();

  // Define column-specific properties map
  const columnPropsMap = useMemo(
    () => ({
      RAISED_FOR: {
        flex: 1,
        autoHeight: true,
        metadata: { isEmployeeSubtitle: true },
      },
      RAISED_BY: {
        metadata: { isEmployeeSubtitle: true },
      },
      DESCRIPTION: {
        minWidth: 400,
      },
    }),
    []
  );

  // Column definitions memoized based on columns
  useEffect(() => {
    if (!columns || columns.length === 0) return;

    const newColumnDefs = columns
      .map((col: ColumnProps) => ({
        colId: col.columnId,
        field: col.columnId,
        id: col.columnId,
        displayName: col.displayName,
        description: col.description,
        headerName: col.displayName,
        minWidth: 220,
        ...getColumnDefinitionByType(col),
        ...(columnPropsMap[col.columnId] || {}),
      }))
      .filter((col) => {
        if (col.field === 'NOTE_ID') return false;

        return true;
      })
      .sort(
        (a, b) =>
          DISCIPLINARY_ACTIONS_COLUMN_ORDER.findIndex((c) => c.colId === a.colId) -
          DISCIPLINARY_ACTIONS_COLUMN_ORDER.findIndex((c) => c.colId === b.colId)
      );

    setColumnDefs(newColumnDefs);
  }, [columns, columnPropsMap]);

  // Handle selection changes
  const onSelectionChanged = useCallback(
    (event) => {
      const api = event.api as GridApi;

      setSelectedDisciplinaryActionIds(api.getSelectedRows().map((e) => +(e?.NOTE_ID || '')));
    },
    [setSelectedDisciplinaryActionIds]
  );

  // Handle approving a disciplinary action
  const handleApprove = useCallback(
    (noteId: number) => {
      evaluateNote({ noteId, body: { evaluation: 'APPROVED' } });
    },
    [evaluateNote]
  );

  const handleApproveAll = useCallback(() => {
    setIsApproveModalOpen(true);
  }, []);

  const handleDeny = useCallback((noteId: number) => {
    setDenyModalState({ open: true, noteId, isDenyAll: false });
  }, []);

  const handleDenyAll = useCallback(() => {
    setDenyModalState({ open: true, noteId: null, isDenyAll: true });
  }, []);

  const handleNotify = useCallback(
    (noteIds: number[], type: 'manager' | 'employee' | null = null) => {
      notifyPeople(noteIds, {
        onSuccess: () => {
          resetSelection();
          handleClearSelection();
          setNotifyEmployeeConfirmationDrawerState({ open: false });
          toast({
            title: type
              ? `Notified ${type} successfully`
              : `${noteIds.length > 1 ? 'Notifications' : 'Notification'} sent successfully`,
            status: 'success',
          });
        },
        onError: (err) => {
          console.error(`Error sending notification`, err);
          toast({
            title: type ? `Error notifying ${type}` : 'Error sending notification',
            status: 'error',
          });
        },
      });
    },
    [notifyPeople, resetSelection, toast, handleClearSelection]
  );

  // Handle notifying employee about a disciplinary action
  const handleNotifyAll = useCallback(() => {
    setNotifyEmployeeConfirmationDrawerState({ open: true });
  }, []);

  const addViolationHandler = () => {
    const queryParams = new URLSearchParams(search);
    queryParams.set(ADD_VIOLATION_DRAWER_QUERY_KEY, 'true');
    history.push({
      search: queryParams.toString(),
    });
  };

  const onRowClicked = useCallback(
    (row: RowClickedEvent) => {
      const { data } = row;
      const queryParams = new URLSearchParams();
      queryParams.set('viewViolation', 'true');
      queryParams.set('violationNoteId', data.NOTE_ID);
      history.push({ search: queryParams.toString() });
    },
    [history]
  );

  // Memoize kebab menu options generator
  const kebabMenuOptionsGenerator = useCallback(
    (data) => {
      const options: AgGridKebabMenuOptions = [];

      if (data?.STATUS === DISCIPLINARY_ACTIONS_STATUS.CREATED) {
        const noteId = data?.NOTE_ID;
        options.push(
          {
            label: 'Approve',
            onClick: () => handleApprove(noteId),
          },
          {
            label: 'Deny',
            onClick: () => handleDeny(noteId),
          }
        );
      } else if (data?.STATUS === DISCIPLINARY_ACTIONS_STATUS.APPROVED) {
        options.push({
          label: 'Notify manager',
          onClick: () => handleNotify([data?.NOTE_ID], 'manager'),
        });
      } else if (data?.STATUS === DISCIPLINARY_ACTIONS_STATUS.PUBLISHED_TO_EMPLOYEE) {
        options.push({
          label: 'Notify employee',
          onClick: () => handleNotify([data?.NOTE_ID], 'employee'),
        });
      }

      return options;
    },
    [handleApprove, handleDeny, handleNotify]
  );

  // Handle selecting all
  const handleSelectAll = useCallback(() => {
    const api = agGridRef.current?.api as GridApi | undefined;
    api?.selectAll('filtered');
  }, [agGridRef]);

  // Error state handling
  if (error) {
    return (
      <EmptyScreen
        heading={
          intentId === 'allDisciplinaryActions' ? 'Actions are not available' : 'Pending actions are not available!'
        }
        description={
          intentId === DISCIPLINARY_ACTIONS_INTENT_ID_MAP.ALL
            ? 'There was an issue fetching the actions.'
            : 'There was an issue fetching the pending actions.'
        }
        illustrationSrc={NoDisciplinaryActions}
      />
    );
  }

  return (
    <>
      <BulkActionHeader
        clearSelection={handleClearSelection}
        selectAll={handleSelectAll}
        handleApproveAll={handleApproveAll}
        handleDenyAll={handleDenyAll}
        handleNotifyAll={handleNotifyAll}
        onAddViolation={addViolationHandler}
        intentId={intentId}
      />
      {filteredRows?.length === 0 && !isFetching ? (
        <Box height="70vh" display="flex" justifyContent="center" alignItems="center">
          <EmptyScreen
            heading={
              intentId === DISCIPLINARY_ACTIONS_INTENT_ID_MAP.PENDING
                ? 'No pending disciplinary actions to approve'
                : 'No disciplinary actions'
            }
            illustrationSrc={NoDisciplinaryActions}
          />
        </Box>
      ) : (
        <Box className={`disciplinary-actions-grid ${intentId}`}>
          <AgGridTable
            key={gridStatePersistenceKey}
            gridStatePersistenceKey={gridStatePersistenceKey}
            ref={agGridRef}
            rowData={filteredRows}
            columnDefs={columnDefs}
            domLayout="normal"
            pagination
            paginationPageSize={10}
            headerHeight={40}
            rowHeight={48}
            rowStyle={{ cursor: 'default' }}
            onSelectionChanged={onSelectionChanged}
            onRowDataUpdated={onRowDataUpdated}
            onFilterChanged={onRowDataUpdated}
            rowSelection={
              intentId === DISCIPLINARY_ACTIONS_INTENT_ID_MAP.PENDING ||
              intentId === DISCIPLINARY_ACTIONS_INTENT_ID_MAP.ALL
                ? { mode: 'multiRow', enableClickSelection: false }
                : undefined
            }
            kebabMenuOptionsGenerator={kebabMenuOptionsGenerator}
            kebabMenuIsRowSpecific
            loading={isFetching}
            onRowClicked={onRowClicked}
            defaultColDef={{
              resizable: false,
            }}
          />
          <ApproveAllConfirmationModal
            isOpen={isApproveModalOpen}
            onClose={() => setIsApproveModalOpen(false)}
            selectedNoteIds={selectedDisciplinaryActionIds}
            rows={filteredRows}
            onApprove={(noteIds) => {
              bulkEvaluateNotes(
                { noteIds, evaluation: 'APPROVED', reason: '' },
                {
                  onSuccess: () => {
                    invalidateDisciplinaryActions();
                    resetSelection();
                    handleClearSelection();
                    setIsApproveModalOpen(false);
                    toast({
                      title: 'Disciplinary notes approved successfully',
                      status: 'success',
                    });
                  },
                  onError: (err) => {
                    console.error('Error approving disciplinary notes', err);
                    toast({
                      title: 'Error approving disciplinary notes',
                      status: 'error',
                    });
                  },
                }
              );
            }}
          />
          <ViewViolationCommentModal
            isOpen={denyModalState.open && !denyModalState.isDenyAll}
            isUpdating={isEvaluateNoteLoading}
            onClose={() => setDenyModalState({ open: false, noteId: null, isDenyAll: false })}
            onSave={(comment) => {
              evaluateNote(
                { noteId: denyModalState.noteId, body: { evaluation: 'DENIED', reason: comment } },
                { onSuccess: () => setDenyModalState({ open: false, noteId: null, isDenyAll: false }) }
              );
            }}
          />
          <DenyAllConfirmationModal
            isOpen={denyModalState.open && denyModalState.isDenyAll}
            onClose={() => setDenyModalState({ open: false, noteId: null, isDenyAll: false })}
            selectedNoteIds={selectedDisciplinaryActionIds}
            rows={filteredRows}
            isDenying={isBulkEvaluateNotesLoading}
            onConfirm={(noteIds, reason) => {
              bulkEvaluateNotes(
                { noteIds, evaluation: 'DENIED', reason },
                {
                  onSuccess: () => {
                    setDenyModalState({ open: false, noteId: null, isDenyAll: false });
                    invalidateDisciplinaryActions();
                    resetSelection();
                    handleClearSelection();
                    toast({
                      title: 'Disciplinary notes denied successfully',
                      status: 'success',
                    });
                  },
                  onError: (err) => {
                    console.error('Error denying disciplinary note', err);
                    toast({
                      title: 'Error denying disciplinary note',
                      status: 'error',
                    });
                  },
                }
              );
            }}
          />
          <NotifyEmployeeConfirmationDrawer
            isOpen={notifyEmployeeConfirmationDrawerState.open}
            onClose={() => setNotifyEmployeeConfirmationDrawerState({ open: false })}
            selectedNoteIds={selectedDisciplinaryActionIds}
            rows={filteredRows}
            onNotify={({ selectedNoteIds }) => handleNotify(selectedNoteIds)}
          />
        </Box>
      )}
    </>
  );
}

export { MyCompanyDisciplinaryActionsGrid };
